/**
 * link-classifier.ts — Authoritative classifier for all user-facing external input.
 *
 * ── SCOPE ─────────────────────────────────────────────────────────────────────
 * Single entry point for classifying any user-pasted, QR-scanned, or deep-linked
 * string BEFORE it reaches the application layer.
 *
 * ── LIBRARY-FIRST RULE ────────────────────────────────────────────────────────
 * Uses existing parseMagnet() and parsePayload() — NEVER reimplements their logic.
 *
 * ── ACCEPTED INPUT (user-facing) ──────────────────────────────────────────────
 * Primary:
 *   magnet:?xt=urn:btih:<40-char hex or 32-char base32>
 *   ${AppConfig.shareBaseUrl}/<8-char code>
 *
 * Secondary (legacy/deep-link):
 *   https://share.app/v1/<base64url payload>
 *   share://v1/<base64url payload>
 *   <40-char hex infoHash>
 *   <64-byte base64url BitDrop packet>
 *
 * ── REJECTED ──────────────────────────────────────────────────────────────────
 * Everything else → kind: 'invalid' with a user-facing reason string.
 */

import { parseMagnet, MagnetParseError } from './magnet-parser';
import { parsePayload } from './parser';
import { PROTOCOL_LINK_PREFIX, PROTOCOL_DEEP_LINK_PREFIX } from './constants';
import { SHORT_URL_ORIGIN, SHORT_URL_CODE_REGEX } from '../links/short-url-constants';

// ── Types ─────────────────────────────────────────────────────────────────────

export type LinkKind =
  | 'magnet'              // Valid magnet URI — use directly
  | 'bitdrop-payload'     // 64-byte base64url BitDrop binary packet
  | 'bitdrop-short-url'   // ${AppConfig.shareBaseUrl}/<code> — needs HTTP resolution
  | 'raw-infohash'        // Bare 40-char hex SHA-1
  | 'invalid';            // Not a recognized BitDrop or magnet link

export interface LinkClassification {
  kind: LinkKind;
  /**
   * Normalized/extracted value for valid kinds:
   *   'magnet'            → the magnet URI verbatim
   *   'bitdrop-short-url' → the full short URL
   *   'raw-infohash'      → lower-cased 40-char hex
   *   'bitdrop-payload'   → the extracted base64url string
   */
  payload?: string;
  /** User-facing reason for 'invalid' kind. Never exposes internal error types. */
  reason?: string;
}

// ── Classifier ────────────────────────────────────────────────────────────────

/**
 * Classify user input into a recognized link kind or 'invalid'.
 *
 * Synchronous. Makes no network requests. Never throws.
 *
 * @param input - Raw user input (pasted, QR-scanned, or from deep link)
 */
export function classifyLink(input: string): LinkClassification {
  const trimmed = (input ?? '').trim();

  // ── 0. Empty ──────────────────────────────────────────────────────────────
  if (!trimmed) {
    return { kind: 'invalid', reason: 'Enter a Magnet or BitDrop link.' };
  }

  // ── 1. Magnet URI ─────────────────────────────────────────────────────────
  if (trimmed.startsWith('magnet:?') || trimmed.startsWith('magnet:')) {
    try {
      parseMagnet(trimmed); // validates xt, hash format, etc.
      return { kind: 'magnet', payload: trimmed };
    } catch (err) {
      const reason = err instanceof MagnetParseError
        ? `This Magnet link is invalid: ${err.message}`
        : 'This Magnet link is invalid.';
      return { kind: 'invalid', reason };
    }
  }

  // ── 2. BitDrop short URL (${AppConfig.shareBaseUrl}/<code>) ────────────────────
  // Use URL parsing + exact origin — never a broad regex match.
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const url = new URL(trimmed);

      if (url.origin === SHORT_URL_ORIGIN) {
        // Must be exactly /<code> with no extra path segments or query params
        const code = url.pathname.slice(1); // strip leading "/"
        if (SHORT_URL_CODE_REGEX.test(code) && !url.search) {
          return { kind: 'bitdrop-short-url', payload: trimmed };
        }
        // Correct origin but unrecognized path — likely landing page or API
        return {
          kind: 'invalid',
          reason: "That doesn't look like a valid BitDrop link.",
        };
      }

      // ── 3. BitDrop full payload URL (https://share.app/v1/<payload>) ──────
      if (trimmed.startsWith(PROTOCOL_LINK_PREFIX)) {
        const payloadStr = trimmed.slice(PROTOCOL_LINK_PREFIX.length);
        return classifyPayloadString(payloadStr);
      }

      // Any other HTTPS URL — not a BitDrop link
      return {
        kind: 'invalid',
        reason: "That doesn't look like a valid Magnet or BitDrop link.",
      };
    } catch {
      return {
        kind: 'invalid',
        reason: "That doesn't look like a valid Magnet or BitDrop link.",
      };
    }
  }

  // ── 4. BitDrop deep link (share://v1/<payload>) ───────────────────────────
  if (trimmed.startsWith(PROTOCOL_DEEP_LINK_PREFIX)) {
    const payloadStr = trimmed.slice(PROTOCOL_DEEP_LINK_PREFIX.length);
    return classifyPayloadString(payloadStr);
  }

  // ── 5. Raw 40-char hex infoHash ───────────────────────────────────────────
  if (/^[0-9a-f]{40}$/i.test(trimmed)) {
    return { kind: 'raw-infohash', payload: trimmed.toLowerCase() };
  }

  // ── 6. BitDrop binary payload (64-byte base64url) ─────────────────────────
  return classifyPayloadString(trimmed);
}

/**
 * Attempt to classify a string as a BitDrop binary payload.
 * Called for bare payload strings and extracted payloads from URLs.
 * Falls back to 'invalid' if parsePayload() rejects it.
 */
function classifyPayloadString(payloadStr: string): LinkClassification {
  try {
    parsePayload(payloadStr);
    return { kind: 'bitdrop-payload', payload: payloadStr };
  } catch {
    return {
      kind: 'invalid',
      reason: "That doesn't look like a valid Magnet or BitDrop link.",
    };
  }
}
